import React from 'react';
import { UIStars } from 'ui/stars';
import { UIAvatar } from 'ui/avatar';
import styles from './ui-trust-quote-strip.module.scss';

export interface UITrustQuoteStripProps {
    quote: string;
    author: {
        name: string;
        photo?: string;
        verifiedLabel?: string;
        /** Renders in place of `verifiedLabel` when provided, e.g. a Google 5-star badge. */
        verifiedBadge?: { src: string; alt: string };
    };
    /** Aggregate rating out of 5. Omit to hide the rating column entirely. */
    rating?: number;
    /** Total verified reviews behind `rating`. Only shown alongside `rating`. */
    reviewsCount?: number;
    className?: string;
}

function getInitial(name: string): string {
    return name.trim().charAt(0).toUpperCase();
}

export function UITrustQuoteStrip({
    quote,
    author,
    rating,
    reviewsCount,
    className = '',
}: UITrustQuoteStripProps) {
    const hasRating = rating !== undefined;

    const classes = [
        styles.strip,
        hasRating ? styles.withRating : '',
        className,
    ].filter(Boolean).join(' ');

    return (
        <div className={classes}>
            {hasRating && (
                <div className={styles.ratingCol}>
                    <UIStars rating={rating} size="lg" />
                    <div className={styles.ratingValue}>
                        {rating.toFixed(1)}
                        <span className={styles.ratingMax}>/ 5</span>
                    </div>
                    {reviewsCount !== undefined && (
                        <span className={styles.reviewsCount}>
                            {reviewsCount.toLocaleString()} verified reviews
                        </span>
                    )}
                </div>
            )}

            <blockquote className={styles.quote}>&ldquo;{quote}&rdquo;</blockquote>

            <div className={styles.authorCol}>
                <UIAvatar
                    size="md"
                    src={author.photo}
                    initials={getInitial(author.name)}
                />
                <div>
                    <strong className={styles.authorName}>{author.name}</strong>
                    {/* Opt-in. This used to default to "Verified buyer",
                        which the caller could not always substantiate — the
                        homepage feeds it Google reviews. No label, no badge. */}
                    {author.verifiedBadge ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                            src={author.verifiedBadge.src}
                            alt={author.verifiedBadge.alt}
                            className={styles.verifiedBadge}
                        />
                    ) : (
                        author.verifiedLabel && (
                            <span className={styles.verified}>
                                {author.verifiedLabel}
                            </span>
                        )
                    )}
                </div>
            </div>
        </div>
    );
}

export default UITrustQuoteStrip;
